import { useAppSelector } from '../../../hooks/useAppSelector';
import * as parentOrganizationsSlice from '../../../data/parent-organizations.slice';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import ParentOrganizationDialog from './ParentOrganizationDialog';
import {
  GridColDef,
  GridRowsProp,
  GridRenderCellParams,
  useGridApiContext,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { DateTime } from 'luxon';
import CardContent from '@mui/material/CardContent';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import {
  ArrowDownward,
  ArrowUpward,
  Clear,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import TextSearch from '../../controls/TextSearch';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../controls/PageHeader';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'ParentOrganizations';

const classes = {
  container: `${PREFIX}-container`,
  cardMain: `${PREFIX}-cardMain`,
  cardHeader: `${PREFIX}-cardHeader`,
  toolbar: `${PREFIX}-toolbar`,
  toolContainer: `${PREFIX}-toolContainer`,
  toolInput: `${PREFIX}-toolInput`,
  grid: `${PREFIX}-grid`,
  previous: `${PREFIX}-previous`,
  groupedName: `${PREFIX}-groupedName`,
  arrowSortButton: `${PREFIX}-arrowSortButton`,
  nameHeader: `${PREFIX}-nameHeader`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    padding: theme.spacing(3),
    gap: theme.spacing(3),
  },
  [`& .${classes.previous}`]: {
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
    color: theme.palette.primary.main,
  },
  [`& .${classes.cardMain}`]: {
    width: '100%',
    padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 13,
  },
  [`& .${classes.cardHeader}`]: {
    padding: `${theme.spacing(1)} 0`,
    '& .MuiCardHeader-title': {
      fontSize: 30,
      fontWeight: 700,
      fontFamily: 'Bitter',
      lineHeight: '40px',
      letterSpacing: '0.25px',
      color: theme.palette.lightActionDisabled90p,
    },
  },
  [`& .${classes.toolbar}`]: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '24px',
  },
  [`& .${classes.toolContainer}`]: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '24px',
    marginBottom: theme.spacing(2),
  },
  [`& .${classes.toolInput}`]: {
    width: '327px',
    borderColor: 'transparent !important',
    borderRadius: '3px',
    background: '#FFF',
    boxShadow: `0px 2px 7px 0px ${theme.palette.lightActionDisabled15p.main}`,
    fontFamily: 'Source Sans Pro',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '24px',
    letterSpacing: '0.15px',
  },
  [`& .${classes.toolInput} fieldset`]: {
    display: 'none',
  },
  [`& .${classes.toolInput} input`]: {
    color: theme.palette.lightActionDisabled60p,
    padding: `${theme.spacing(1.5)} 0`,

    '&::placeholder': {
      color: theme.palette.lightActionDisabled60p,
    },
  },
  [`& .${classes.toolInput} .MuiSelect-select`]: {
    padding: theme.spacing(1.5),
    color: '#000',
  },
  [`& .${classes.grid}`]: {
    border: 'none',
    flexGrow: 'unset',
    height: 'fit-content',
    '&.empty': {
      height: 300,
    },
    '& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeaderTitleContainerContent, & .MuiDataGrid-columnHeaderTitleContainerContent .MuiTypography-root':
      {
        width: '100%',
        color: theme.palette.lightActionDisabled90p,
        fontFamily: 'Source Sans Pro',
        fontSize: 14,
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: 24,
        letterSpacing: 0.17,
      },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    '& .MuiDataGrid-columnHeader[data-field="name"] .MuiDataGrid-iconButtonContainer':
      {
        width: 'unset',
        marginLeft: `${theme.spacing(-17.5)} !important`,
      },
    '& .MuiDataGrid-columnHeader[data-field="name"] .MuiDataGrid-sortIcon': {
      opacity: '1 !important',
      visibility: 'visible !important',
      transition: 'none !important',
    },
    [`& .MuiDataGrid-row`]: {
      letterSpacing: '0.17px',
      cursor: 'pointer',
      '& .MuiDataGrid-cell:last-child': {
        flex: 1,
        display: 'flex',
        justifyContent: 'flex-end',
      },
    },
  },
  '& .MuiCardContent-root': {
    padding: 0,
  },
  [`& .${classes.groupedName}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    fontSize: '14px',
    letterSpacing: '0.17px',
    lineHeight: '20px',
  },
  [`& .${classes.nameHeader}`]: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    gap: '4px',
  },
  [`& .${classes.arrowSortButton}`]: {
    color: theme.palette.lightActionDisabled60p.main,
  },
}));

const CustomGridTreeDataGroupingCell = (params: GridRenderCellParams) => {
  const theme = useTheme();
  const { id, field, row, rowNode } = params;
  const isParent = row.type === 'parent';
  const isClient = row.type === 'client';
  const isExpanded = (rowNode as any).childrenExpanded;
  const apiRef = useGridApiContext();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        pl: isClient ? 4 : 0,
      }}>
      {isParent && (rowNode as any).children?.length && (
        <IconButton
          size="small"
          onClick={(event) => {
            if (rowNode.type !== 'group') return;

            apiRef.current.setRowChildrenExpansion(id, !isExpanded);
            apiRef.current.setCellFocus(id, field);
            event.stopPropagation();
          }}
          sx={{ mr: 1, p: 0.5 }}>
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      )}

      <Box
        className={classes.groupedName}
        sx={{
          paddingLeft: isClient ? 3 : 0,
        }}>
        <span style={{ color: theme.palette.lightActionDisabled90p.main }}>
          {row.name}{' '}
        </span>
        <span style={{ color: theme.palette.lightActionDisabled60p.main }}>
          {row.identifier}
        </span>
      </Box>
    </Box>
  );
};

export default function ParentOrganizations() {
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const isSuperReaderUser = useIsSuperReaderUser();
  const [parentOrganizationDialogOpen, setPrarentOrganizationDialogOpen] =
    useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedParentIds, setExpandedParentIds] = useState<string[]>([]);
  const [hasExpanded, setHasExpanded] = useState(false);
  const [nameSortDirection, setNameSortDirection] = useState<'asc' | 'desc'>(
    'asc',
  );
  const [modifiedSortDirection, setModifiedSortDirection] = useState<
    'asc' | 'desc'
  >('asc');
  const [activeSortColumn, setActiveSortColumn] = useState<
    'name' | 'modified'
  >();

  const dispatch = useAppDispatch();
  const parentOrganizations = useAppSelector(
    parentOrganizationsSlice.selectParentOrganizations,
  );

  useEffect(() => {
    dispatch(parentOrganizationsSlice.fetchParentOrganizationsAsync());
  }, []);
  const { t } = useTranslation('common');

  const handleCreateParentOrganization = useCallback(() => {
    setPrarentOrganizationDialogOpen(true);
  }, []);

  const handleParentOrganizationDialogClose = useCallback(() => {
    setPrarentOrganizationDialogOpen(false);
  }, []);

  const onSearchChange = (val: string) => {
    setSearchQuery(val);
  };

  const sortedParentOrganizations = useMemo(() => {
    return [...parentOrganizations].sort((a, b) => {
      const aVal = a.parentOrganizationName?.toLowerCase() || '';
      const bVal = b.parentOrganizationName?.toLowerCase() || '';
      return aVal.localeCompare(bVal);
    });
  }, [parentOrganizations]);

  const filteredParentOrganizations = useMemo(() => {
    const normalizedSearch = searchQuery.toLowerCase();
    const matchedParentIds: string[] = [];

    let result = sortedParentOrganizations
      .map((org) => {
        const parentMatches = [
          org.parentOrganizationName,
          org.parentOrganizationBC,
          DateTime.fromISO(org.modifiedDateTimeUTC || '').toLocaleString(
            DateTime.DATE_SHORT,
          ),
        ].some((field) => field?.toLowerCase().includes(normalizedSearch));

        const matchedClients = org.clients?.filter((client) =>
          client.legalName?.toLowerCase().includes(normalizedSearch),
        );

        if (parentMatches || (matchedClients && matchedClients.length > 0)) {
          if (matchedClients && matchedClients.length > 0 && !parentMatches) {
            matchedParentIds.push(org.parentOrganizationBC);
          }
          return {
            ...org,
            clients: parentMatches ? org.clients : matchedClients,
          };
        }

        return null;
      })
      .filter(Boolean) as typeof parentOrganizations;

    result = result.sort((a, b) => {
      if (activeSortColumn === 'name') {
        const aVal = a.parentOrganizationName?.toLowerCase() || '';
        const bVal = b.parentOrganizationName?.toLowerCase() || '';
        return nameSortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      if (activeSortColumn === 'modified') {
        const aDate = a.modifiedDateTimeUTC || '';
        const bDate = b.modifiedDateTimeUTC || '';
        return modifiedSortDirection === 'asc'
          ? aDate.localeCompare(bDate)
          : bDate.localeCompare(aDate);
      }

      return 0;
    });

    setExpandedParentIds(matchedParentIds);
    return result;
  }, [
    sortedParentOrganizations,
    searchQuery,
    nameSortDirection,
    modifiedSortDirection,
    activeSortColumn,
  ]);

  useEffect(() => {
    if (!apiRef.current || expandedParentIds.length === 0) return;

    const timeout = setTimeout(() => {
      const allRowIds = apiRef.current.getAllRowIds?.() ?? [];

      expandedParentIds.forEach((id) => {
        if (allRowIds.includes(id)) {
          apiRef.current.setRowChildrenExpansion(id, true);
        }
      });
    }, 100);

    return () => clearTimeout(timeout);
  }, [expandedParentIds]);

  useEffect(() => {
    if (hasExpanded || expandedParentIds.length === 0) return;

    const timeout = setTimeout(() => {
      const allRowIds = apiRef.current.getAllRowIds?.() ?? [];

      expandedParentIds.forEach((id) => {
        if (allRowIds.includes(id)) {
          apiRef.current.setRowChildrenExpansion(id, true);
        }
      });

      setHasExpanded(true);
    }, 100);

    return () => clearTimeout(timeout);
  }, [expandedParentIds, hasExpanded]);

  const getTreeData = (): GridRowsProp => {
    return filteredParentOrganizations.flatMap((org) => {
      const parentRow = {
        id: org.parentOrganizationBC,
        name: org.parentOrganizationName,
        identifier: org.parentOrganizationBC,
        modified: DateTime.fromISO(
          org.modifiedDateTimeUTC || '',
        ).toLocaleString(DateTime.DATE_SHORT),
        type: 'parent',
        hierarchy: [org.parentOrganizationBC],
      };

      const clientRows = org.clients?.map((client) => ({
        id: `${org.parentOrganizationBC}-${client.clientBC}`,
        parentId: org.parentOrganizationBC,
        clientId: client.clientBC,
        name: client.legalName,
        identifier: client.clientBC,
        modified: DateTime.fromISO(
          client.modifiedDateTimeUTC || '',
        ).toLocaleString(DateTime.DATE_SHORT),
        type: 'client',
        hierarchy: [org.parentOrganizationBC, client.clientBC],
      }));

      return [parentRow, ...(clientRows || [])];
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'modified',
      headerName: t('parentOrganizations.modified'),
      width: 200,
      sortable: false,
      renderHeader: () => (
        <Box
          className={classes.nameHeader}
          onClick={() => {
            setActiveSortColumn('modified');
            setModifiedSortDirection((prev) =>
              prev === 'asc' ? 'desc' : 'asc',
            );
          }}>
          {t('parentOrganizations.modified')}
          <IconButton size="small" className={classes.arrowSortButton}>
            {activeSortColumn === 'modified' &&
            modifiedSortDirection === 'asc' ? (
              <ArrowUpward fontSize="small" />
            ) : (
              <ArrowDownward fontSize="small" />
            )}
          </IconButton>
        </Box>
      ),
      renderCell: (params) => <Box>{params.value}</Box>,
    },
  ];

  return (
    <StyledBox className={classes.container} maxWidth="xl">
      <Card className={classes.cardMain}>
        <PageHeader
          title={`${t('parentOrganizations.searchTitle')}`}
          returnLink="/settings"
          returnLinkText={t('settings.title')}
        />
        <CardContent>
          <Box className={classes.toolbar}>
            <Box className={classes.toolContainer}>
              <TextSearch
                id="text-search"
                icon={<SearchIcon />}
                label={t('client.search')}
                currentSearchFilter={searchQuery}
                onSearchChange={onSearchChange}
              />
            </Box>
            {!isSuperReaderUser && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateParentOrganization}>
                {t('parentOrganizations.add')}
              </Button>
            )}
          </Box>
          <DataGridPro
            apiRef={apiRef}
            rows={getTreeData()}
            getRowId={(row) => row.id}
            treeData
            groupingColDef={{
              headerName: t('parentOrganizations.name'),
              width: 400,
              minWidth: 200,
              sortable: false,
              renderHeader: () => (
                <Box
                  className={classes.nameHeader}
                  onClick={() => {
                    setActiveSortColumn('name');
                    setNameSortDirection((prev) =>
                      prev === 'asc' ? 'desc' : 'asc',
                    );
                  }}>
                  {t('parentOrganizations.name')}
                  <IconButton size="small" className={classes.arrowSortButton}>
                    {activeSortColumn === 'name' &&
                    nameSortDirection === 'asc' ? (
                      <ArrowUpward fontSize="small" />
                    ) : (
                      <ArrowDownward fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              ),
              renderCell: CustomGridTreeDataGroupingCell,
            }}
            getTreeDataPath={(row) => row.hierarchy}
            columns={columns as GridColDef[]}
            className={`${classes.grid} ${
              getTreeData().length === 0 ? 'empty' : ''
            }`}
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            hideFooterRowCount
            hideFooter
            rowHeight={56}
            disableColumnMenu={true}
            disableColumnResize={true}
            onCellClick={(params, event) => {
              const id =
                params.row.type === 'parent'
                  ? encodeURIComponent(params.row.id)
                  : encodeURIComponent(params.row.clientId);
              params.row.type === 'parent'
                ? navigate(`/parent-organizations/${id}`)
                : navigate(`/clients/${id}`);
            }}
          />
        </CardContent>
      </Card>
      <ParentOrganizationDialog
        title={t('parentOrganizations.createParentOrganization')}
        open={parentOrganizationDialogOpen}
        onClose={handleParentOrganizationDialogClose}
      />
    </StyledBox>
  );
}
